/**
 * The authenticated app shell (the `_authenticated` layout component;
 * component-inventory.md §2 `AppShell` = TopNav + Sidebar + main area). It keeps the
 * Sprint-1 auth/permission gating intact — the firm branding read stays behind
 * `firm.read` (ENG-10) so non-admins get the default accent, never a 403. The PWA update banner
 * (§10) is NOT rendered here: it lives in the root layout (routes.tsx) so public routes show it too.
 *
 * The firm's `primary_colour` (US-003) is exposed as `--argus-firm-accent` on the shell root;
 * only the top-bar accent line and the brand mark consume it — buttons and links keep the
 * design-system primary so a firm colour can never hurt contrast on interactive controls.
 *
 * `children` defaults to the matched route's `<Outlet/>` — the only thing `_authenticated`
 * itself ever passes. The root route's 404 (`notFoundComponent`, PO finding 2026-10-05 #5)
 * renders OUTSIDE that route match, so it fills this slot explicitly to get the same chrome
 * for a signed-in, firm-bound user on an unknown path.
 */
import { Outlet } from '@tanstack/react-router';
import type { CSSProperties, JSX, ReactNode } from 'react';

import { useFirmSettings } from '../hooks/use-firm-settings.ts';
import { Sidebar } from './layouts/sidebar.tsx';
import { TopNav } from './layouts/top-nav.tsx';

export function AppShell({ children }: { children?: ReactNode | undefined } = {}): JSX.Element {
  // Gated on firm.read inside the hook (ENG-10); shared with the TopNav logo mark (F-29).
  const { data: firm } = useFirmSettings();

  const style = (
    firm?.primary_colour ? { '--argus-firm-accent': firm.primary_colour } : {}
  ) as CSSProperties;

  return (
    <div className="argus-app" style={style}>
      <TopNav />
      <div className="argus-app__frame">
        <Sidebar />
        <main id="main" className="argus-app__main">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
